import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ArrowLeft, CheckCircle2, Languages, LifeBuoy, LoaderCircle, Send } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import type { AppUser } from '../../types';
import { submitSupportRequest, type FeedbackDevice, type SupportCategory } from '../../backend/userRequestService';

type Language = 'en' | 'hi';
const text = (language: Language, english: string, hindi: string) =>
  language === 'hi' ? hindi : english;

const inputClass = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-blue-500';
const selectClass = `${inputClass} cursor-pointer`;

const initialDevice = (): FeedbackDevice => {
  const platform = Capacitor.getPlatform();
  if (platform === 'android') return 'Android';
  if (platform === 'ios') return 'iPhone';
  return 'Laptop/Desktop';
};
const initialVersion = () => typeof navigator === 'undefined' ? 'Web app' : navigator.userAgent.slice(0, 300);

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block space-y-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
    <span>{label}</span>
    {children}
  </label>;
}

export function AppSupportPage({
  currentUser,
  onOpenAuth,
  onClose
}: {
  currentUser: AppUser | null;
  onOpenAuth: () => void;
  onClose: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [language, setLanguage] = useState<Language>('en');
  const [name, setName] = useState(currentUser?.displayName || '');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [category, setCategory] = useState<SupportCategory>('technical');
  const [subject, setSubject] = useState('');
  const [details, setDetails] = useState('');
  const [device, setDevice] = useState<FeedbackDevice>(initialDevice);
  const [otherDeviceDetails, setOtherDeviceDetails] = useState('');
  const [appVersion, setAppVersion] = useState(initialVersion);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!name && currentUser?.displayName) setName(currentUser.displayName);
    if (!email && currentUser?.email) setEmail(currentUser.email);
  }, [currentUser?.displayName, currentUser?.email, email, name]);

  // Jump to top after a successful submit so the confirmation is visible.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [submitted]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError('');
    if (!currentUser) {
      onOpenAuth();
      return;
    }
    setIsSubmitting(true);
    try {
      await submitSupportRequest({ name, email, category, subject, details, device, otherDeviceDetails, appVersion });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Support request could not be sent.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const categories: Array<[SupportCategory, string, string]> = [
    ['account', 'Account and profile', 'अकाउंट और प्रोफ़ाइल'],
    ['login', 'Sign-in or verification', 'साइन-इन या वेरिफ़िकेशन'],
    ['video', 'Videos and uploads', 'वीडियो और अपलोड'],
    ['safety', 'Safety or reporting', 'सुरक्षा या रिपोर्ट'],
    ['technical', 'App problem', 'ऐप की समस्या'],
    ['other', 'Something else', 'अन्य']
  ];

  const topBtn = 'inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  return (
    // Own scroll container: works even if the app root/body has overflow:hidden or a fixed height.
    <div
      ref={scrollRef}
      className="fixed inset-0 z-[100] overflow-y-auto overscroll-contain bg-slate-100 text-slate-900 dark:bg-slate-950 dark:text-slate-100"
      style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
    >
      <header
        className="sticky top-0 z-10 border-b border-slate-200 bg-slate-100/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90"
        style={{ paddingTop: 'var(--sat)' }}
      >
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-3 py-3 sm:px-6">
          <button type="button" onClick={onClose} className={topBtn}>
            <ArrowLeft className="h-4 w-4" />{text(language, 'Back to YeTryKar', 'YeTryKar पर वापस जाएँ')}
          </button>
          <button type="button" onClick={() => setLanguage(value => value === 'en' ? 'hi' : 'en')} className={topBtn}>
            <Languages className="h-4 w-4" />{language === 'en' ? 'हिन्दी' : 'English'}
          </button>
        </div>
      </header>

      <main
        className="mx-auto max-w-2xl px-3 pt-5 sm:px-6"
        style={{ paddingBottom: 'calc(var(--sab) + 32px)' }}
      >
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 px-5 py-7 text-white sm:px-8">
            <LifeBuoy className="h-8 w-8 text-blue-300" />
            <h1 className="mt-3 text-2xl font-black tracking-tight">{text(language, 'App Support', 'ऐप सहायता')}</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">{text(language, 'Tell us what you need help with. Our support team will review your request.', 'आपको किस बात में मदद चाहिए, हमें बताएँ। हमारी सपोर्ट टीम आपकी रिक्वेस्ट देखेगी।')}</p>
          </div>

          <div className="space-y-5 p-5 sm:p-8">
            <aside className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-slate-800 dark:border-blue-900 dark:bg-blue-950/50 dark:text-slate-200">
              <p className="font-bold">{text(language, 'Support email', 'सपोर्ट ईमेल')}: <a className="text-blue-700 underline dark:text-blue-400" href="mailto:support@yetrykar.com">support@yetrykar.com</a></p>
              <p className="mt-2 leading-relaxed">{text(language, 'We aim to resolve your issue or send you a progress update within 7 working days (Monday–Friday, excluding public holidays). Complex issues may take longer; we will keep you informed.', 'हम 7 कार्य-दिवसों के भीतर (सोमवार–शुक्रवार, सार्वजनिक छुट्टियों को छोड़कर) समस्या हल करने या प्रगति की जानकारी देने का प्रयास करेंगे। जटिल समस्याओं में अधिक समय लग सकता है; हम आपको अपडेट देते रहेंगे।')}</p>
              <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">{text(language, 'Please do not include passwords, OTPs, or payment card details.', 'कृपया पासवर्ड, OTP या पेमेंट कार्ड की जानकारी साझा न करें।')}</p>
            </aside>

            {submitted ? (
              <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">
                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
                <h2 className="mt-3 text-lg font-bold">{text(language, 'Support request received', 'सपोर्ट रिक्वेस्ट मिल गई')}</h2>
                <p className="mt-2 text-sm">{text(language, 'Your request has been saved securely. We will contact you by email with an update within 7 working days.', 'आपकी रिक्वेस्ट सुरक्षित रूप से सेव हो गई है। हम 7 कार्य-दिवसों के भीतर ईमेल से अपडेट देंगे।')}</p>
                <button type="button" onClick={onClose} className="mt-5 rounded-lg bg-blue-700 px-4 py-2 text-sm font-bold text-white hover:bg-blue-800">{text(language, 'Return to app', 'ऐप पर वापस जाएँ')}</button>
              </div>
            ) : (
              <form onSubmit={event => void submit(event)} className="space-y-4">
                {!currentUser && <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
                  {text(language, 'Sign in to send a support request.', 'सपोर्ट रिक्वेस्ट भेजने के लिए साइन इन करें।')}
                  <button type="button" onClick={onOpenAuth} className="ml-2 font-bold underline">{text(language, 'Sign in', 'साइन इन')}</button>
                </p>}

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={text(language, 'Your name', 'आपका नाम')}>
                    <input className={inputClass} required maxLength={100} value={name} onChange={event => setName(event.target.value)} />
                  </Field>
                  <Field label={text(language, 'Email address for our reply', 'जवाब के लिए ईमेल पता')}>
                    <input className={inputClass} required type="email" maxLength={254} value={email} onChange={event => setEmail(event.target.value)} />
                  </Field>
                </div>

                <Field label={text(language, 'What do you need help with?', 'आपको किस विषय में मदद चाहिए?')}>
                  <select className={selectClass} value={category} onChange={event => setCategory(event.target.value as SupportCategory)}>
                    {categories.map(([value, english, hindi]) => <option key={value} value={value}>{text(language, english, hindi)}</option>)}
                  </select>
                </Field>

                <Field label={text(language, 'Subject', 'विषय')}>
                  <input className={inputClass} required maxLength={160} value={subject} onChange={event => setSubject(event.target.value)} />
                </Field>

                <Field label={text(language, 'Describe the issue or question', 'समस्या या सवाल का विवरण दें')}>
                  <textarea className={inputClass} required rows={6} maxLength={3000} value={details} onChange={event => setDetails(event.target.value)} />
                </Field>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={text(language, 'Device used', 'इस्तेमाल किया गया डिवाइस')}>
                    <select className={selectClass} value={device} onChange={event => setDevice(event.target.value as FeedbackDevice)}>
                      <option value="Android">Android</option>
                      <option value="iPhone">iPhone</option>
                      <option value="Laptop/Desktop">{text(language, 'Laptop/Desktop', 'लैपटॉप/डेस्कटॉप')}</option>
                      <option value="Other">{text(language, 'Other', 'अन्य')}</option>
                    </select>
                  </Field>
                  <Field label={text(language, 'Browser / app version', 'ब्राउज़र / ऐप वर्ज़न')}>
                    <input className={inputClass} required maxLength={300} value={appVersion} onChange={event => setAppVersion(event.target.value)} />
                  </Field>
                </div>

                {device === 'Other' && <Field label={text(language, 'Please specify your device', 'अपने डिवाइस का नाम बताएँ')}>
                  <input className={inputClass} required maxLength={100} value={otherDeviceDetails} onChange={event => setOtherDeviceDetails(event.target.value)} />
                </Field>}

                <p className="text-xs text-slate-500 dark:text-slate-400">{text(language, 'Need to attach a screenshot or recording? Email it to support@yetrykar.com and include the same subject.', 'स्क्रीनशॉट या रिकॉर्डिंग भेजनी है? support@yetrykar.com पर ईमेल करें और यही विषय लिखें।')}</p>

                {submitError && <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200">{submitError}</p>}

                <button type="submit" disabled={isSubmitting || !currentUser} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-700 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50">
                  {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {text(language, isSubmitting ? 'Sending…' : 'Send support request', isSubmitting ? 'भेजा जा रहा है…' : 'सपोर्ट रिक्वेस्ट भेजें')}
                </button>
                <p className="text-center text-xs text-slate-500 dark:text-slate-400">{text(language, 'Your request is private and visible only to authorized YeTryKar administrators.', 'आपकी रिक्वेस्ट निजी है और केवल अधिकृत YeTryKar एडमिन देख सकते हैं।')}</p>
              </form>
            )}
          </div>
        </section>

        <footer className="py-5 text-center text-xs text-slate-500 dark:text-slate-400">{text(language, 'Team YeTryKar Support', 'YeTryKar सपोर्ट टीम')} · <a className="font-semibold text-blue-700 underline dark:text-blue-400" href="mailto:support@yetrykar.com">support@yetrykar.com</a></footer>
      </main>
    </div>
  );
}